import {
    STORES,
    getAll,
    getByIndex,
    putAll,
    put,
    clearStore
} from './indexeddb.js';

import {
    syncPendingOrders,
    syncPendingCustomers,
    startAutoSync
} from './sync.js';

let networkHealthy = navigator.onLine;
let consecutiveFailures = 0;
const MAX_FAILURES = 3;


/*
|--------------------------------------------------------------------------
| NETWORK EVENTS
|--------------------------------------------------------------------------
*/

function dispatchOfflineEvent() {
    window.dispatchEvent(new Event('offline'));
}

function dispatchOnlineEvent() {
    window.dispatchEvent(new Event('online'));
}


/*
|--------------------------------------------------------------------------
| NETWORK HEALTH CHECK
|--------------------------------------------------------------------------
*/

async function checkNetworkHealth() {

    if (!navigator.onLine) {

        if (networkHealthy) {

            networkHealthy = false;

            dispatchOfflineEvent();
        }

        return;
    }

    try {

        const controller =
            new AbortController();

        const timeoutId =
            setTimeout(
                () => controller.abort(),
                5000
            );

        const response =
            await fetch(
                'https://www.gstatic.com/generate_204',
                {
                    method: 'GET',
                    mode: 'no-cors',
                    cache: 'no-store',
                    signal: controller.signal,
                }
            );

        clearTimeout(timeoutId);

        if (
            response.type === 'opaque' ||
            response.ok
        ) {

            consecutiveFailures = 0;

            if (!networkHealthy) {

                networkHealthy = true;

                dispatchOnlineEvent();
            }

        } else {

            throw new Error(
                'Health check failed: ' +
                response.status
            );
        }

    } catch (e) {

        consecutiveFailures++;

        if (
            consecutiveFailures >= MAX_FAILURES &&
            networkHealthy
        ) {

            networkHealthy = false;

            dispatchOfflineEvent();
        }
    }
}


function startNetworkMonitor(
    intervalMs = 5000
) {

    setInterval(
        checkNetworkHealth,
        intervalMs
    );

    checkNetworkHealth();
}


window.startNetworkMonitor =
    startNetworkMonitor;

window.checkNetworkHealth =
    checkNetworkHealth;


/*
|--------------------------------------------------------------------------
| CLEAN DATA
|--------------------------------------------------------------------------
*/

function cleanData(data) {

    return JSON.parse(
        JSON.stringify(data)
    );
}


/*
|--------------------------------------------------------------------------
| CACHE API DATA
|--------------------------------------------------------------------------
*/

async function cacheApiData() {

    try {

        const response =
            await fetch('/api/pos/data');

        if (!response.ok) {

            return false;
        }

        const data =
            await response.json();

        await clearStore(
            STORES.categories
        );

        await clearStore(
            STORES.menuItems
        );

        await clearStore(
            STORES.sections
        );

        await clearStore(
            STORES.customers
        );


        if (data.categories?.length) {

            await putAll(
                STORES.categories,
                cleanData(
                    data.categories
                )
            );
        }


        if (data.menuItems?.length) {

            await putAll(
                STORES.menuItems,
                cleanData(
                    data.menuItems
                )
            );
        }


        if (data.sections?.length) {

            await putAll(
                STORES.sections,
                cleanData(
                    data.sections
                )
            );
        }


        if (data.customers?.length) {

            await putAll(
                STORES.customers,
                cleanData(
                    data.customers
                )
            );
        }


        return true;

    } catch (e) {

        console.warn(
            '[Offline] Failed to cache API data:',
            e
        );

        return false;
    }
}


/*
|--------------------------------------------------------------------------
| OFFLINE POS
|--------------------------------------------------------------------------
*/

function offlinePosData() {

    return {

        /*
        |--------------------------------------------------------------------------
        | NETWORK
        |--------------------------------------------------------------------------
        */

        isOffline:
            !navigator.onLine,

        offlineReady:
            false,


        /*
        |--------------------------------------------------------------------------
        | DATA
        |--------------------------------------------------------------------------
        */

        categories: [],

        menuItems: [],

        allItems: [],

        sections: [],

        customers: [],


        /*
        |--------------------------------------------------------------------------
        | CATEGORY
        |--------------------------------------------------------------------------
        */

        selectedCategory: null,


        /*
        |--------------------------------------------------------------------------
        | CART
        |--------------------------------------------------------------------------
        */

        cart: [],

        /*
        |--------------------------------------------------------------------------
        | IMPORTANT
        |
        | This is a COPY of the cart used by the receipt.
        | It survives after this.cart is cleared.
        |--------------------------------------------------------------------------
        */

        printReceiptOrder: [],


        /*
        |--------------------------------------------------------------------------
        | ORDER
        |--------------------------------------------------------------------------
        */

        orderType:
            'takeaway',

        selectedSection:
            null,

        selectedTable:
            null,

        selectedCustomer:
            null,


        /*
        |--------------------------------------------------------------------------
        | ITEM MODAL
        |--------------------------------------------------------------------------
        */

        selectedItem:
            null,

        selectedOptions:
            {},

        qty:
            1,

        showItemModal:
            false,

        showCartDrawer:
            false,


        /*
        |--------------------------------------------------------------------------
        | Make it a Meal
        |--------------------------------------------------------------------------
        |
        | Completely separate from optionGroups / variants.
        |
        */

        makeYourOwnMeal:
            false,

        makeYourOwnMealPrice:
            0,


        /*
        |--------------------------------------------------------------------------
        | NOTE
        |--------------------------------------------------------------------------
        */

        showNoteModal:
            false,

        noteText:
            '',

        noteItemIndex:
            null,


        /*
        |--------------------------------------------------------------------------
        | CUSTOMER
        |--------------------------------------------------------------------------
        */

        showPostcodeModal:
            false,

        showCustomerModal:
            false,

        customerName:
            '',

        customerPhone:
            '',

        customerAddress:
            '',

        customerPostCode:
            '',

        customerEmail:
            '',


        /*
        |--------------------------------------------------------------------------
        | PAYMENT
        |--------------------------------------------------------------------------
        */

        showPaymentModal:
            false,

        paymentMethod:
            'cash',

        amountPaid:
            0,
        
        change:
            0,
        
        receiptAmountPaid:
            0,
        
        receiptChange:
            0,


        /*
        |--------------------------------------------------------------------------
        | SYNC
        |--------------------------------------------------------------------------
        */

        pendingCount:
            0,

        syncMessage:
            '',


        /*
        |--------------------------------------------------------------------------
        | INIT
        |--------------------------------------------------------------------------
        */

        async init() {

            /*
            |--------------------------------------------------------------------------
            | ONLINE EVENT
            |--------------------------------------------------------------------------
            */

            window.addEventListener(
                'online',
                async () => {

                    this.isOffline =
                        false;

                    this.syncMessage =
                        '';


                    /*
                    |--------------------------------------------------------------------------
                    | Sync customers FIRST
                    |--------------------------------------------------------------------------
                    */

                    const customerResult =
                        await syncPendingCustomers();


                    /*
                    |--------------------------------------------------------------------------
                    | Reload customers
                    |--------------------------------------------------------------------------
                    */

                    this.customers =
                        cleanData(
                            await getAll(
                                STORES.customers
                            )
                        );


                    /*
                    |--------------------------------------------------------------------------
                    | Refresh API cache
                    |--------------------------------------------------------------------------
                    */

                    await cacheApiData();


                    /*
                    |--------------------------------------------------------------------------
                    | Sync orders
                    |--------------------------------------------------------------------------
                    */

                    const result =
                        await syncPendingOrders();


                    if (result.synced > 0) {

                        let message =
                            result.synced +
                            ' order(s) synced!';


                        if (
                            customerResult.synced >
                            0
                        ) {

                            message +=
                                ' ' +
                                customerResult.synced +
                                ' customer(s) synced!';
                        }


                        this.syncMessage =
                            message;


                        await this.updatePendingCount();


                        setTimeout(
                            () => {

                                this.syncMessage =
                                    '';

                            },
                            4000
                        );


                    } else if (
                        customerResult.synced > 0
                    ) {

                        this.syncMessage =
                            customerResult.synced +
                            ' customer(s) synced!';


                        setTimeout(
                            () => {

                                this.syncMessage =
                                    '';

                            },
                            4000
                        );
                    }
                }
            );


            /*
            |--------------------------------------------------------------------------
            | OFFLINE EVENT
            |--------------------------------------------------------------------------
            */

            window.addEventListener(
                'offline',
                () => {

                    this.isOffline =
                        true;
                }
            );


            /*
            |--------------------------------------------------------------------------
            | Start network monitor
            |--------------------------------------------------------------------------
            */

            startNetworkMonitor();


            /*
            |--------------------------------------------------------------------------
            | Load IndexedDB
            |--------------------------------------------------------------------------
            */

            await this.loadFromDB();


            /*
            |--------------------------------------------------------------------------
            | Auto sync
            |--------------------------------------------------------------------------
            */

            startAutoSync();


            /*
            |--------------------------------------------------------------------------
            | Refresh cache if online
            |--------------------------------------------------------------------------
            */

            if (navigator.onLine) {

                const cached =
                    await cacheApiData();


                if (cached) {

                    await this.loadFromDB();
                }
            }
        },


        /*
        |--------------------------------------------------------------------------
        | LOAD FROM DB
        |--------------------------------------------------------------------------
        */

        async loadFromDB() {

            try {

                this.categories =
                    cleanData(
                        await getAll(
                            STORES.categories
                        )
                    );


                this.allItems =
                    cleanData(
                        await getAll(
                            STORES.menuItems
                        )
                    );


                this.sections =
                    cleanData(
                        await getAll(
                            STORES.sections
                        )
                    );


                this.customers =
                    cleanData(
                        await getAll(
                            STORES.customers
                        )
                    );


                /*
                |--------------------------------------------------------------------------
                | Load cart
                |--------------------------------------------------------------------------
                */

                const cartItems =
                    cleanData(
                        await getAll(
                            STORES.cart
                        )
                    );


                this.cart =
                    cartItems.sort(
                        (a, b) =>
                            a.idx - b.idx
                    );

                this.enforceNoDeliveryOrderType();


                /*
                |--------------------------------------------------------------------------
                | Category
                |--------------------------------------------------------------------------
                */

                if (
                    this.categories.length >
                    0
                ) {

                    this.selectedCategory =
                        this.categories[0].id;

                    this.filterItems();
                }


                this.offlineReady =
                    this.categories.length >
                    0;


                await this.updatePendingCount();

            } catch (e) {

                console.warn(
                    '[Offline] Failed to load from DB:',
                    e
                );
            }
        },


        /*
        |--------------------------------------------------------------------------
        | FILTER ITEMS
        |--------------------------------------------------------------------------
        */

        filterItems() {

            if (this.selectedCategory) {

                this.menuItems =
                    this.allItems.filter(
                        (d) =>
                            d.category_id ===
                            this.selectedCategory
                    );

            } else {

                this.menuItems =
                    this.allItems;
            }
        },


        /*
        |--------------------------------------------------------------------------
        | SELECT CATEGORY
        |--------------------------------------------------------------------------
        */

        get selectedCategoryData() {
            return this.categories.find(
                cat => Number(cat.id) === Number(this.selectedCategory)
            ) || null;
        },

        selectCategory(id) {

            this.selectedCategory =
                id;

            this.filterItems();
        },


        /*
        |--------------------------------------------------------------------------
        | PAYMENT CALCULATION
        |--------------------------------------------------------------------------
        */

        updatePayment() {

            const total = Number(this.getCartTotal());

            this.amountPaid = Number(this.amountPaid || 0);

            this.change = Math.max(
                0,
                this.amountPaid - total
            );
        },

        /*
        |--------------------------------------------------------------------------
        | OPEN ITEM
        |--------------------------------------------------------------------------
        */

        openItem(item) {

            this.selectedItem =
                cleanData(item);
        
            this.qty = 1;
        
            this.selectedOptions = {};
        
            /*
            |----------------------------------------------------------------------
            | Initialize option groups
            |----------------------------------------------------------------------
            */
        
            for (
                const group of
                (this.selectedItem.option_groups || [])
            ) {
        
                this.selectedOptions[group.id] =
                    group.is_multiple
                        ? []
                        : null;
            }
        
            /*
            |----------------------------------------------------------------------
            | Make it a Meal
            |----------------------------------------------------------------------
            */
        
            this.makeYourOwnMeal = false;
        
            this.makeYourOwnMealPrice = 0;
        
            this.showItemModal = true;
        },


        /*
        |--------------------------------------------------------------------------
        | CLOSE ITEM
        |--------------------------------------------------------------------------
        */

        closeItem() {

            this.showItemModal =
                false;

            this.selectedItem =
                null;

            this.selectedOptions =
                {};

            /*
            |--------------------------------------------------------------------------
            | Reset Make it a Meal
            |--------------------------------------------------------------------------
            */

            this.makeYourOwnMeal =
                false;

            this.makeYourOwnMealPrice =
                0;

            this.qty =
                1;
        },


        /*
        |--------------------------------------------------------------------------
        | TOGGLE Make it a Meal
        |--------------------------------------------------------------------------
        */

        toggleMakeYourOwnMeal() {

            if (
                this.selectedItem &&
                this.selectedItem.make_your_own_meal
            ) {

                this.makeYourOwnMeal =
                    !this.makeYourOwnMeal;

                this.makeYourOwnMealPrice =
                    this.makeYourOwnMeal
                        ? parseFloat(
                            this.selectedItem.make_your_own_meal_price || 0
                        )
                        : 0;
            }
        },


        /*
        |--------------------------------------------------------------------------
        | QUANTITY
        |--------------------------------------------------------------------------
        */

        increment() {

            this.qty++;
        },


        decrement() {

            if (this.qty > 1) {

                this.qty--;
            }
        },


        /*
        |--------------------------------------------------------------------------
        | CAN ADD TO CART
        |--------------------------------------------------------------------------
        */

        get canAddToCart() {

            if (!this.selectedItem) {
                return false;
            }
        
            const groups =
                this.selectedItem.option_groups || [];
        
            return groups.every((group) => {
        
                if (!group.is_required) {
                    return true;
                }
        
                const selected =
                    this.selectedOptions[group.id];
        
                if (group.is_multiple) {
                    return Array.isArray(selected) &&
                        selected.length > 0;
                }
        
                return selected !== undefined &&
                    selected !== null &&
                    selected !== '';
            });
        },


        /*
        |--------------------------------------------------------------------------
        | ADD TO CART
        |--------------------------------------------------------------------------
        */

        async addToCart() {

            const dish =
                cleanData(
                    this.selectedItem
                );

            if (!dish) {

                return;
            }

            const cartOptions =
                [];

            let basePrice =
                parseFloat(
                    dish.price
                );

            let extraPrice =
                0;

            /*
            |--------------------------------------------------------------------------
            | Make it a Meal Price
            |--------------------------------------------------------------------------
            */

            let makeYourOwnMealPrice =
                0;

            /*
            |--------------------------------------------------------------------------
            | Process Existing Option Groups
            |--------------------------------------------------------------------------
            */

            for (
                const option of
                (
                    dish.option_groups ||
                    []
                )
            ) {

                const selected =
                    this.selectedOptions[
                        option.id
                    ];

                if (
                    selected === undefined ||
                    selected === null
                ) {

                    continue;
                }

                const values =
                    Array.isArray(selected)
                        ? selected
                        : [selected];

                for (
                    const valueId
                    of values
                ) {

                    const val =
                        option.values.find(
                            (v) =>
                                v.id ==
                                valueId
                        );

                    if (!val) {

                        continue;
                    }

                    cartOptions.push({

                        group_name:
                            String(
                                option.name
                            ),

                        name:
                            String(
                                val.name
                            ),

                        price:
                            Number(
                                val.price
                            ),
                    });

                    /*
                    |--------------------------------------------------------------------------
                    | Existing Variant Pricing
                    |--------------------------------------------------------------------------
                    */

                    if (
                        option.name
                            .toLowerCase() ===
                        'size'
                    ) {

                        basePrice =
                            parseFloat(
                                val.price
                            );

                    } else {

                        extraPrice +=
                            parseFloat(
                                val.price
                            );
                    }
                }
            }

            /*
            |--------------------------------------------------------------------------
            | Make it a Meal
            |--------------------------------------------------------------------------
            |
            | COMPLETELY SEPARATE FROM VARIANTS.
            |
            */

            if (
                this.makeYourOwnMeal === true &&
                dish.make_your_own_meal === true
            ) {

                makeYourOwnMealPrice =
                    parseFloat(
                        dish.make_your_own_meal_price || 0
                    );
            }

            /*
            |--------------------------------------------------------------------------
            | Final Item Price
            |--------------------------------------------------------------------------
            */

            const finalPrice =
                basePrice +
                extraPrice +
                makeYourOwnMealPrice;

            /*
            |--------------------------------------------------------------------------
            | Cart Merge Key
            |--------------------------------------------------------------------------
            |
            | Make it a Meal must be part of the identity
            | of the cart item.
            |
            */

            const optionsKey =
                JSON.stringify({

                    options:
                        [...cartOptions]
                            .sort(
                                (a, b) =>
                                    a.group_name
                                        .localeCompare(
                                            b.group_name
                                        ) ||
                                    a.name
                                        .localeCompare(
                                            b.name
                                        )
                            ),

                    make_your_own_meal:
                        this.makeYourOwnMeal,
                });

            let found =
                false;

            for (
                const item of
                this.cart
            ) {

                const existingKey =
                    JSON.stringify({

                        options:
                            [...(item.options ||
                                [])]
                                .sort(
                                    (a, b) =>
                                        a.group_name
                                            .localeCompare(
                                                b.group_name
                                            ) ||
                                        a.name
                                            .localeCompare(
                                                b.name
                                            )
                                ),

                        make_your_own_meal:
                            item.make_your_own_meal ||
                            false,
                    });

                if (
                    item.id === dish.id &&
                    existingKey === optionsKey
                ) {

                    item.qty +=
                        this.qty;

                    found =
                        true;

                    break;
                }
            }

            if (!found) {

                this.cart.push(
                    cleanData({

                        idx:
                            Date.now(),

                        id:
                            Number(
                                dish.id
                            ),

                        name:
                            String(
                                dish.name
                            ),

                        price:
                            Number(
                                finalPrice
                            ),

                        qty:
                            Number(
                                this.qty
                            ),

                        tag: dish.tag
                            ? cleanData(dish.tag)
                            : null,

                        options:
                            cartOptions,


                        /*
                        |--------------------------------------------------------------------------
                        | Make it a Meal
                        |--------------------------------------------------------------------------
                        */

                        make_your_own_meal:
                            this.makeYourOwnMeal,

                        make_your_own_meal_price:
                            makeYourOwnMealPrice,
                    })
                );
            }

            await this.persistCart();

            this.enforceNoDeliveryOrderType();

            this.closeItem();
        },


        /*
        |--------------------------------------------------------------------------
        | CART QUANTITY
        |--------------------------------------------------------------------------
        */

        incrementItem(index) {

            this.cart[index].qty++;

            this.persistCart();
        },


        decrementItem(index) {

            if (
                this.cart[index].qty >
                1
            ) {

                this.cart[index].qty--;

                this.persistCart();
            }
        },


        /*
        |--------------------------------------------------------------------------
        | REMOVE CART ITEM
        |--------------------------------------------------------------------------
        */

        removeItem(index) {
            this.cart.splice(index, 1);
        
            this.enforceNoDeliveryOrderType();
        
            this.persistCart();
        },


        /*
        |--------------------------------------------------------------------------
        | NOTES
        |--------------------------------------------------------------------------
        */

        openNote(index) {

            this.noteItemIndex =
                index;

            this.noteText =
                this.cart[index].note ||
                '';

            this.showNoteModal =
                true;
        },


        saveNote() {

            if (
                this.noteItemIndex !==
                null
            ) {

                this.cart[
                    this.noteItemIndex
                ].note =
                    String(
                        this.noteText
                    );
            }

            this.showNoteModal =
                false;

            this.noteText =
                '';

            this.persistCart();
        },


        /*
        |--------------------------------------------------------------------------
        | PERSIST CART
        |--------------------------------------------------------------------------
        */

        async persistCart() {

            try {

                await clearStore(
                    STORES.cart
                );

                for (
                    const item
                    of this.cart
                ) {

                    const cleanItem =
                        cleanData(item);

                    if (
                        !cleanItem.idx
                    ) {

                        cleanItem.idx =
                            Date.now() +
                            Math.random();
                    }

                    await put(
                        STORES.cart,
                        cleanItem
                    );
                }

            } catch (e) {

                console.error(
                    '[Offline] persistCart error:',
                    e
                );
            }
        },


        /*
        |--------------------------------------------------------------------------
        | CHECK CART FOR NO DELIVERY ITEMS
        |--------------------------------------------------------------------------
        */

        get hasNoDeliveryItems() {
            return this.cart.some((item) => {
                const menuItem = this.allItems.find(
                    dish => Number(dish.id) === Number(item.id)
                );

                const tagName =
                    item.tag?.name ||
                    menuItem?.tag?.name ||
                    item.tag_name ||
                    '';

                return tagName.trim().toLowerCase() === 'no delivery';
            });
        },


        /*
        |--------------------------------------------------------------------------
        | ENFORCE EAT-IN FOR RESTRICTED ITEMS
        |--------------------------------------------------------------------------
        */

        enforceNoDeliveryOrderType() {
            if (!this.hasNoDeliveryItems) {
                return;
            }

            this.orderType = 'eat-in';

            this.selectedTable = null;

            if (
                !this.selectedSection ||
                !this.sections.some(
                    section => Number(section.id) === Number(this.selectedSection)
                )
            ) {
                this.selectedSection = this.sections[0]?.id ?? null;
            }
        },

        /*
        |--------------------------------------------------------------------------
        | ORDER TYPE
        |--------------------------------------------------------------------------
        */

        setOrderType(type) {
            if (!['eat-in', 'takeaway', 'delivery'].includes(type)) {
                return false;
            }

            // Restricted items are Eat-In only.
            if (this.hasNoDeliveryItems && type !== 'eat-in') {
                this.enforceNoDeliveryOrderType();

                return false;
            }

            this.orderType = type;

            if (type !== 'eat-in') {
                this.selectedSection = null;
                this.selectedTable = null;
            } else if (this.sections.length > 0) {
                this.selectedSection = this.sections[0].id;
            }

            if (type !== 'delivery') {
                this.selectedCustomer = null;
            }

            return true;
        },


        /*
        |--------------------------------------------------------------------------
        | TABLES
        |--------------------------------------------------------------------------
        */

        getTablesForSection() {

            if (
                !this.selectedSection
            ) {

                return [];
            }

            const section =
                this.sections.find(
                    (s) =>
                        s.id ==
                        this.selectedSection
                );

            return section
                ? cleanData(
                    section.tables || []
                )
                : [];
        },


        /*
        |--------------------------------------------------------------------------
        | CART TOTAL
        |--------------------------------------------------------------------------
        */

        getCartTotal() {

            return this.cart
                .reduce(
                    (sum, item) =>
                        sum +
                        Number(item.price) *
                        Number(item.qty),
                    0
                )
                .toFixed(2);
        },


        /*
        |--------------------------------------------------------------------------
        | PLACE ORDER
        |--------------------------------------------------------------------------
        |
        | IMPORTANT FLOW:
        |
        | 1. Copy cart
        | 2. Build order from copied cart
        | 3. Save pending order
        | 4. Print receipt
        | 5. Clear real cart
        |
        |--------------------------------------------------------------------------
        */

        async placeOrder() {

            try {

                /*
                |--------------------------------------------------------------------------
                | Make sure cart has items
                |--------------------------------------------------------------------------
                */

                if (
                    this.cart.length ===
                    0
                ) {

                    return;
                }

                /*
                |--------------------------------------------------------------------------
                | STEP 1
                |
                | COPY CART BEFORE CLEARING IT
                |--------------------------------------------------------------------------
                */

                this.printReceiptOrder =
                    cleanData(
                        this.cart
                    );

                this.receiptAmountPaid =
                    Number(this.amountPaid || 0);

                this.receiptChange =
                    Number(this.change || 0);


                console.log(
                    '[Offline] Receipt cart:',
                    this.printReceiptOrder
                );

                /*
                |--------------------------------------------------------------------------
                | STEP 2
                |
                | Calculate total from copied cart
                |--------------------------------------------------------------------------
                */

                const total =
                    this.printReceiptOrder
                        .reduce(
                            (
                                sum,
                                item
                            ) =>
                                sum +
                                Number(
                                    item.price
                                ) *
                                Number(
                                    item.qty
                                ),
                            0
                        );

                /*
                |--------------------------------------------------------------------------
                | CUSTOMER
                |--------------------------------------------------------------------------
                */

                const customer =
                    this.selectedCustomer
                        ? this.customers.find(
                            c =>
                                c.id ===
                                this.selectedCustomer
                        )
                        : null;

                const isNewOfflineCustomer =
                    customer &&
                    !customer.original_offline_id;

                /*
                |--------------------------------------------------------------------------
                | CUSTOMER FIELDS
                |--------------------------------------------------------------------------
                */

                const orderCustomerFields =
                    {};

                if (
                    this.orderType ===
                        'delivery' &&
                    this.selectedCustomer
                ) {

                    orderCustomerFields.customer_id =
                        this.selectedCustomer;

                    if (
                        isNewOfflineCustomer
                    ) {

                        orderCustomerFields.offline_customer_id =
                            this.selectedCustomer;

                        orderCustomerFields.customer_phone =
                            customer.phone_number;

                        orderCustomerFields.customer_name =
                            customer.name;
                    }
                }

                /*
                |--------------------------------------------------------------------------
                | STEP 3
                |
                | BUILD ORDER FROM printReceiptOrder
                |--------------------------------------------------------------------------
                */

                const order =
                    cleanData({

                        offline_id:
                            'offline_' +
                            Date.now() +
                            '_' +
                            Math.random()
                                .toString(36)
                                .substring(
                                    2,
                                    9
                                ),

                        total:
                            Number(total),

                        payment_method:
                            this.paymentMethod,

                        payment_status:
                            'pending',

                        order_type:
                            String(
                                this.orderType
                            ),

                        table_id:
                            this.selectedTable ||
                            null,

                        ...orderCustomerFields,

                        items:
                            this.printReceiptOrder
                                .map(
                                    (item) => ({

                                        menu_item_id:
                                            Number(
                                                item.id
                                            ),

                                        item_name:
                                            String(
                                                item.name
                                            ),

                                        price:
                                            Number(
                                                item.price
                                            ),

                                        qty:
                                            Number(
                                                item.qty
                                            ),

                                        note:
                                            item.note
                                                ? String(
                                                    item.note
                                                )
                                                : null,

                                        options:
                                            cleanData(
                                                item.options ||
                                                []
                                            ),

                                        /*
                                        |--------------------------------------------------------------------------
                                        | Make it a Meal
                                        |--------------------------------------------------------------------------
                                        */

                                        make_your_own_meal:
                                            item.make_your_own_meal ||
                                            false,

                                        make_your_own_meal_price:
                                            item.make_your_own_meal_price ||
                                            0,
                                    })
                                ),

                        created_at:
                            new Date()
                                .toISOString(),
                    });

                console.log(
                    '[Offline] Order:',
                    order
                );

                /*
                |--------------------------------------------------------------------------
                | STEP 4
                |
                | SAVE ORDER TO INDEXEDDB
                |--------------------------------------------------------------------------
                */

                await put(
                    STORES.pendingOrders,
                    order
                );

                /*
                |--------------------------------------------------------------------------
                | STEP 5
                |
                | GENERATE AND PRINT RECEIPT
                |--------------------------------------------------------------------------
                */

                // Generate receipt HTML from the order data
                this.generateAndPrintReceipt(order, total);

                /*
                |--------------------------------------------------------------------------
                | STEP 6
                |
                | NOW CLEAR REAL CART
                |--------------------------------------------------------------------------
                */

                this.cart = [];

                await clearStore(
                    STORES.cart
                );

                /*
                |--------------------------------------------------------------------------
                | Update pending count
                |--------------------------------------------------------------------------
                */

                await this.updatePendingCount();

                /*
                |--------------------------------------------------------------------------
                | STEP 7
                |
                | Sync if online
                |--------------------------------------------------------------------------
                */

                if (
                    navigator.onLine
                ) {

                    const customerResult =
                        await syncPendingCustomers();

                    const orderResult =
                        await syncPendingOrders();

                    if (
                        orderResult.synced >
                        0
                    ) {

                        let message =
                            'Order placed and synced!';

                        if (
                            customerResult.synced >
                            0
                        ) {

                            message +=
                                ' ' +
                                customerResult.synced +
                                ' customer(s) synced!';
                        }

                        this.syncMessage =
                            message;

                        await this.updatePendingCount();

                    } else {

                        this.syncMessage =
                            'Order saved. Will sync when online.';
                    }

                } else {

                    this.syncMessage =
                        'Order saved offline. Will sync when online.';
                }

                /*
                |--------------------------------------------------------------------------
                | Clear message
                |--------------------------------------------------------------------------
                */

                setTimeout(
                    () => {

                        this.syncMessage =
                            '';

                    },
                    4000
                );

            } catch (e) {

                console.error(
                    '[Offline] placeOrder error:',
                    e
                );
            }
        },

        /*
        |--------------------------------------------------------------------------
        | GENERATE AND PRINT RECEIPT
        |--------------------------------------------------------------------------
        */

        generateAndPrintReceipt(order, total) {

            try {
            
                /*
                |--------------------------------------------------------------------------
                | SECTION / TABLE
                |--------------------------------------------------------------------------
                */
            
                let sectionName = '';
                let tableName = '';
            
                if (this.orderType === 'eat-in') {
            
                    const section = this.sections.find(
                        s => s.id == this.selectedSection
                    );
            
                    if (section) {
            
                        sectionName = section.name || '';
            
                        const tables = section.tables || [];
            
                        const table = tables.find(
                            t => t.id == this.selectedTable
                        );
            
                        if (table) {
                            tableName = table.name || '';
                        }
                    }
                }
            
            
                /*
                |--------------------------------------------------------------------------
                | CUSTOMER
                |--------------------------------------------------------------------------
                */
            
                let customerName = '';
            
                if (this.selectedCustomer) {
            
                    const customer = this.customers.find(
                        c => c.id == this.selectedCustomer
                    );
            
                    if (customer) {
                        customerName = customer.name || '';
                    }
                }
            
            
                /*
                |--------------------------------------------------------------------------
                | ORDER NUMBER
                |--------------------------------------------------------------------------
                */
            
                const orderNumber =
                    order?.id ??
                    order?.offline_id ??
                    '';
            
            
                /*
                |--------------------------------------------------------------------------
                | BUILD RECEIPT CONTENT
                |--------------------------------------------------------------------------
                */
            
                const receiptHTML = `
            
                    <div style="
                        width: 55mm;
                        max-width: 55mm;
                        margin: 0;
                        padding: 5px;
                        background: #ffffff;
                        color: #000000;
                        font-family: monospace;
                        font-size: 10px;
                        line-height: 1.3;
                        box-sizing: border-box;
                    ">
            
            
                        <!-- HEADER -->
            
                        <div style="
                            text-align: center;
                            margin-bottom: 6px;
                        ">
            
                            <div style="
                                font-size: 15px;
                                font-weight: bold;
                            ">
                                SiBi
                            </div>
            
                            <div style="
                                font-size: 10px;
                                font-weight: bold;
                            ">
                                SiBi POS
                            </div>
            
                            <div style="
                                font-size: 12px;
                                font-weight: bold;
                                margin-top: 4px;
                            ">
                                Sales Receipt
                            </div>
            
                        </div>
            
            
                        <!-- DIVIDER -->
            
                        <div style="
                            border-top: 1px dashed #000;
                            margin: 4px 0;
                        "></div>
            
            
                        <!-- ORDER INFORMATION -->
            
                        <div style="
                            font-size: 9px;
                            line-height: 1.5;
                        ">
            
                            <div style="
                                display: flex;
                                justify-content: space-between;
                                gap: 5px;
                            ">
                                <span>Order #:</span>
                                <strong>${orderNumber}</strong>
                            </div>
            
            
                            <div style="
                                display: flex;
                                justify-content: space-between;
                                gap: 5px;
                            ">
                                <span>Date:</span>
                                <span>${new Date().toLocaleString()}</span>
                            </div>
            
            
                            <div style="
                                display: flex;
                                justify-content: space-between;
                                gap: 5px;
                            ">
                                <span>Type:</span>
                                <strong>${this.orderType || ''}</strong>
                            </div>
            
            
                            ${
                                sectionName
                                    ? `
                                        <div style="
                                            display: flex;
                                            justify-content: space-between;
                                            gap: 5px;
                                        ">
                                            <span>Section:</span>
                                            <strong>${sectionName}</strong>
                                        </div>
                                    `
                                    : ''
                            }
            
            
                            ${
                                tableName
                                    ? `
                                        <div style="
                                            display: flex;
                                            justify-content: space-between;
                                            gap: 5px;
                                        ">
                                            <span>Table:</span>
                                            <strong>${tableName}</strong>
                                        </div>
                                    `
                                    : ''
                            }
            
            
                            ${
                                customerName
                                    ? `
                                        <div style="
                                            display: flex;
                                            justify-content: space-between;
                                            gap: 5px;
                                        ">
                                            <span>Customer:</span>
                                            <strong>${customerName}</strong>
                                        </div>
                                    `
                                    : ''
                            }
            
        
            
                        </div>
            
            
                        <!-- DIVIDER -->
            
                        <div style="
                            border-top: 1px dashed #000;
                            margin: 5px 0;
                        "></div>
            
            
                        <!-- ITEMS -->
            
                        <div>
            
                            ${
                                (this.printReceiptOrder || [])
                                    .map(item => {
            
                                        const itemPrice =
                                            Number(item.price || 0);
            
                                        const itemQty =
                                            Number(item.qty || 0);
            
                                        const itemTotal =
                                            itemPrice * itemQty;
            
                                        const mealPrice =
                                            Number(
                                                item.make_your_own_meal_price || 0
                                            );
            
            
                                        /*
                                        |--------------------------------------------------------------------------
                                        | OPTIONS
                                        |--------------------------------------------------------------------------
                                        */
            
                                        const optionsHTML =
                                            Array.isArray(item.options)
                                                ? item.options
                                                    .map(option => {
            
                                                        const optionName =
                                                            option.name ||
                                                            option.option_name ||
                                                            '';
            
                                                        const optionPrice =
                                                            Number(
                                                                option.price || 0
                                                            );
            
                                                        return `
            
                                                            <div style="
                                                                margin-left: 8px;
                                                                font-size: 9px;
                                                                line-height: 1.35;
                                                            ">
            
                                                                + ${optionName}
            
                                                                ${
                                                                    optionPrice > 0
                                                                        ? ` (£${optionPrice.toFixed(2)})`
                                                                        : ''
                                                                }
            
                                                            </div>
            
                                                        `;
            
                                                    })
                                                    .join('')
                                                : '';
            
            
                                        /*
                                        |--------------------------------------------------------------------------
                                        | NOTE
                                        |--------------------------------------------------------------------------
                                        */
            
                                        const notesHTML =
                                            item.note
                                                ? `
            
                                                    <div style="
                                                        margin-left: 8px;
                                                        font-size: 9px;
                                                        font-style: italic;
                                                        line-height: 1.35;
                                                    ">
            
                                                        Note: ${item.note}
            
                                                    </div>
            
                                                `
                                                : '';
            
            
                                        /*
                                        |--------------------------------------------------------------------------
                                        | ITEM
                                        |--------------------------------------------------------------------------
                                        */
            
                                        return `
            
                                            <div style="
                                                margin-bottom: 6px;
                                                page-break-inside: avoid;
                                                break-inside: avoid;
                                            ">
            
            
                                                <div style="
                                                    font-weight: bold;
                                                    font-size: 10px;
                                                    line-height: 1.25;
                                                    word-break: break-word;
                                                ">
            
                                                    ${item.name || ''}
            
                                                </div>
            
            
                                                <div style="
                                                    display: flex;
                                                    justify-content: space-between;
                                                    align-items: flex-start;
                                                    gap: 5px;
                                                    font-size: 9px;
                                                    line-height: 1.35;
                                                ">
            
                                                    <span>
                                                        ${itemQty} × £${itemPrice.toFixed(2)}
                                                    </span>
            
                                                    <strong>
                                                        £${itemTotal.toFixed(2)}
                                                    </strong>
            
                                                </div>
            
            
                                                ${
                                                    mealPrice > 0
                                                        ? `
            
                                                            <div style="
                                                                margin-left: 8px;
                                                                font-size: 9px;
                                                            ">
            
                                                                Make it a Meal:
                                                                £${mealPrice.toFixed(2)}
            
                                                            </div>
            
                                                        `
                                                        : ''
                                                }
            
            
                                                ${optionsHTML}
            
            
                                                ${notesHTML}
            
                                            </div>
            
                                        `;
            
                                    })
                                    .join('')
                            }
            
                        </div>
            
            
                        <!-- DIVIDER -->
            
                        <div style="
                            border-top: 1px dashed #000;
                            margin: 5px 0;
                        "></div>
            
            
                        <!-- TOTAL -->
            
                        <div style="
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            gap: 5px;
                            font-size: 13px;
                            font-weight: bold;
                            margin: 5px 0;
                        ">
            
                            <span>TOTAL</span>
            
                            <span>
                                £${Number(total || 0).toFixed(2)}
                            </span>
            
                        </div>
            
<div style="
                                display: flex;
                                justify-content: space-between;
                                gap: 5px;
                            ">
                                <span>Payment:</span>
                                <strong>
                                    ${order?.payment_method || 'Cash'}
                                </strong>
                            </div>

                            ${
                                (order?.payment_method || '').toLowerCase() === 'cash'
                                    ? `
                                        <div style="
                                            display: flex;
                                            justify-content: space-between;
                                            gap: 5px;
                                        ">
                                            <span>Paid:</span>
                                            <strong>
                                                £${Number(this.receiptAmountPaid || 0).toFixed(2)}
                                            </strong>
                                        </div>

                                        <div style="
                                            display: flex;
                                            justify-content: space-between;
                                            gap: 5px;
                                        ">
                                            <span>Change:</span>
                                            <strong>
                                                £${Number(this.receiptChange || 0).toFixed(2)}
                                            </strong>
                                        </div>
                                    `
                                    : ''
                            }
                        <!-- DIVIDER -->
            
                        <div style="
                            border-top: 1px dashed #000;
                            margin: 5px 0;
                        "></div>
            
            
                        <!-- FOOTER -->
            
                        <div style="
                            text-align: center;
                            font-size: 9px;
                            line-height: 1.4;
                            margin-top: 5px;
                        ">
            
                            <div style="font-weight: bold;">
                                Thank you!
                            </div>
            
                            <div>
                                Please visit us again.
                            </div>
            
                            <div style="
                                margin-top: 4px;
                                font-size: 8px;
                            ">
                                SiBi POS
                            </div>
            
                        </div>
            
                    </div>
            
                `;
            
            
                /*
                |--------------------------------------------------------------------------
                | USE THE SAME PRINT IMPLEMENTATION
                |--------------------------------------------------------------------------
                |
                | IMPORTANT:
                | We do NOT create another iframe here.
                | printContent() handles:
                |
                | - 55mm width
                | - automatic receipt height
                | - @page height
                | - iframe
                | - printing
                | - cleanup
                |
                */
            
                this.printContent(
                    receiptHTML,
                    'Receipt',
                    false
                );
            
            
            } catch (e) {
            
                console.error(
                    '[Offline] generateAndPrintReceipt error:',
                    e
                );
            
            }
            
            },

            printContent(content, title, isKitchen = false) {

                const printFrame = document.createElement('iframe');
            
                printFrame.style.position = 'fixed';
                printFrame.style.left = '-9999px';
                printFrame.style.top = '0';
                printFrame.style.width = '55mm';
                printFrame.style.border = '0';
            
                document.body.appendChild(printFrame);
            
                const doc = printFrame.contentWindow.document;
            
                doc.open();
            
                doc.write(`
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
            
                        <title>${title}</title>
            
                        <style>
            
                            @page {
                                size: 55mm auto;
                                margin: 0;
                            }
            
                            * {
                                box-sizing: border-box;
                            }
            
                            html {
                                width: 55mm;
                                margin: 0;
                                padding: 0;
                            }
            
                            body {
                                width: 55mm;
                                margin: 0;
                                padding: 0;
                                font-family: monospace;
                                font-size: 10px;
                                line-height: 1.3;
                            }
            
                            .receipt {
                                width: 55mm;
                                margin: 0;
                                padding: 5px;
                            }
            
                            hr {
                                border: none;
                                border-top: 1px dashed #000;
                                margin: 4px 0;
                            }
            
                            .text-center {
                                text-align: center;
                            }
            
                            h2 {
                                font-size: 13px;
                                margin: 0 0 2px 0;
                            }
            
                            h4 {
                                font-size: 11px;
                                margin: 4px 0;
                            }
            
                            p {
                                margin: 2px 0;
                            }
            
                            small {
                                font-size: 9px;
                                display: block;
                                margin-left: 8px;
                            }
            
                            /* Kitchen receipt */
                            .divider {
                                border-top: 1px dashed #000;
                                margin: 4px 0;
                            }
            
                            .center {
                                text-align: center;
                            }
            
                            .header {
                                text-align: center;
                                font-weight: bold;
                                font-size: 12px;
                            }
            
                            .title {
                                font-size: 13px;
                                font-weight: bold;
                            }
            
                            .row {
                                display: flex;
                                justify-content: space-between;
                                margin: 2px 0;
                            }
            
                            .item {
                                font-weight: bold;
                                margin-top: 4px;
                            }
            
                            .item-main {
                                display: flex;
                                gap: 4px;
                            }
            
                            .qty {
                                font-weight: bold;
                            }
            
                            .name {
                                text-transform: uppercase;
                            }
            
                            .option {
                                margin-left: 10px;
                                font-size: 9px;
                            }
            
                            .notes {
                                font-weight: bold;
                                font-size: 10px;
                            }
            
                            .note-text {
                                font-weight: normal;
                                margin-top: 2px;
                            }
            
                            .footer {
                                text-align: center;
                                margin-top: 6px;
                                font-size: 10px;
                            }
            
                            /*
                             * IMPORTANT:
                             * Don't force a minimum height.
                             * Don't set height: 100%.
                             */
                            @media print {
            
                                html,
                                body {
                                    width: 55mm;
                                    height: auto !important;
                                    min-height: 0 !important;
                                    margin: 0 !important;
                                    padding: 0 !important;
                                }
            
                                .receipt {
                                    width: 55mm;
                                    height: auto !important;
                                    min-height: 0 !important;
                                    margin: 0 !important;
                                }
                            }
            
                        </style>
                    </head>
            
                    <body>
            
                        <div class="receipt">
                            ${content}
                        </div>
            
                    </body>
                    </html>
                `);
            
                doc.close();
            
                /*
                 * Wait until the receipt has actually rendered.
                 */
                setTimeout(() => {
            
                    const body = printFrame.contentWindow.document.body;
            
                    /*
                     * Get the REAL content height.
                     */
                    const height = Math.ceil(
                        Math.max(
                            body.scrollHeight,
                            body.offsetHeight,
                            body.clientHeight
                        )
                    );
            
                    /*
                     * Set iframe height to exactly the receipt height.
                     */
                    printFrame.style.height = `${height}px`;
            
                    /*
                     * Tell the print document its exact height.
                     */
                    const style = printFrame.contentWindow.document.createElement('style');
            
                    style.innerHTML = `
                        @page {
                            size: 55mm ${height}px;
                            margin: 0;
                        }
            
                        html,
                        body {
                            width: 55mm;
                            height: ${height}px !important;
                            min-height: 0 !important;
                            margin: 0 !important;
                            padding: 0 !important;
                        }
            
                        .receipt {
                            width: 55mm;
                            height: ${height}px !important;
                            min-height: 0 !important;
                        }
                    `;
            
                    printFrame.contentWindow.document.head.appendChild(style);
            
                    /*
                     * Print.
                     */
                    printFrame.contentWindow.focus();
            
                    printFrame.contentWindow.print();
            
                    /*
                     * Clean up.
                     */
                    setTimeout(() => {
                        printFrame.remove();
                    }, 1000);
            
                }, 150);
            
            },
            


        /*
        |--------------------------------------------------------------------------
        | PENDING COUNT
        |--------------------------------------------------------------------------
        */

        async updatePendingCount() {

            const pending =
                await getAll(
                    STORES.pendingOrders
                );

            this.pendingCount =
                pending.length;
        },


        /*
        |--------------------------------------------------------------------------
        | SAVE CUSTOMER
        |--------------------------------------------------------------------------
        */

        async saveCustomer() {

            if (
                !this.customerName ||
                !this.customerPhone
            ) {

                return;
            }

            const offlineId =
                'customer_' +
                Date.now() +
                '_' +
                Math.random()
                    .toString(36)
                    .substring(
                        2,
                        9
                    );

            const customer = {

                id:
                    Date.now(),

                offline_id:
                    offlineId,

                name:
                    this.customerName,

                phone_number:
                    this.customerPhone,

                email:
                    this.customerEmail ||
                    null,
            };

            /*
            |--------------------------------------------------------------------------
            | Main customers store
            |--------------------------------------------------------------------------
            */

            await put(
                STORES.customers,
                customer
            );

            /*
            |--------------------------------------------------------------------------
            | Pending customers
            |--------------------------------------------------------------------------
            */

            await put(
                STORES.pendingCustomers,
                {

                    offline_id:
                        offlineId,

                    name:
                        this.customerName,

                    phone_number:
                        this.customerPhone,

                    email:
                        this.customerEmail ||
                        null,

                    address:
                        this.customerAddress ||
                        null,

                    postcode:
                        this.customerPostCode ||
                        null,

                    action:
                        'create',
                }
            );

            /*
            |--------------------------------------------------------------------------
            | Update local customers
            |--------------------------------------------------------------------------
            */

            this.customers.push(
                customer
            );

            this.selectedCustomer =
                customer.id;

            /*
            |--------------------------------------------------------------------------
            | Reset form
            |--------------------------------------------------------------------------
            */

            this.customerName =
                '';

            this.customerPhone =
                '';

            this.customerEmail =
                '';

            this.customerAddress =
                '';

            this.customerPostCode =
                '';

            this.showCustomerModal =
                false;
        },
    };
}


/*
|--------------------------------------------------------------------------
| GLOBAL
|--------------------------------------------------------------------------
*/

window.offlinePosData =
    offlinePosData;

window.cacheApiData =
    cacheApiData;


export {
    offlinePosData,
    cacheApiData
};